import { call, put } from "redux-saga/effects";

import { authorizedRequest, HTTP_METHODS } from "utils/request";
import { generic } from "utils/action";

export class AuthorizedRequestBuilder {
  /**
   * @param successAction {string}
   * @param failedAction {string}
   */
  constructor(successAction, failedAction) {
    this.successAction = successAction;
    this.failedAction = failedAction;
    this.options = {
      url: "",
      method: HTTP_METHODS.GET,
      body: null,
      callbackBuilder: null,
      resultKey: "result",
      resultValue: null,
      debug: false,
      debugText: "",
    };
  }

  /**
   * When called enabled debugging for this request.
   * @param {string} text a string to identify this request.
   * @return {AuthorizedRequestBuilder} this
   */
  debug(text = "") {
    this.options.debug = true;
    this.options.debugText = text;
    return this;
  }

  /**
   * The http method used for this request
   * @param {string} method the http method
   * @return {AuthorizedRequestBuilder} this
   */
  method(method) {
    this.options.method = method;
    return this;
  }

  /**
   * Add a body to the request
   * @param body {function | object} either the body to send or a function which
   * will build the body to send.
   * @return {AuthorizedRequestBuilder} this
   */
  body(body) {
    this.options.body = body;
    return this;
  }

  /**
   * Change the key opn the resulting action where the request result will be set.
   * @param value {string} the key
   * @return {AuthorizedRequestBuilder} this
   */
  resultKey(value) {
    this.options.resultKey = value;
    return this;
  }

  /**
   * Set the resulting value which will be dispatched with the result action.
   * @param value {function | any} Either the resulting value or a function building
   * the resulting value.
   * @return {AuthorizedRequestBuilder} this
   */
  result(value) {
    this.options.resultValue = value;
    return this;
  }

  /**
   * set the url for this request.
   * @param value {function | string} the url string or url builder function
   * @return {AuthorizedRequestBuilder} this
   */
  url(value) {
    this.options.url = value;
    return this;
  }

  /**
   * Add a result callback to the request.
   * @param value {function} the function which returns the callback that will be
   * called after the request completes.
   * @return {AuthorizedRequestBuilder} this
   */
  callbackBuilder(value) {
    this.options.callbackBuilder = value;
    return this;
  }

  build() {
    const {
      method,
      resultKey,
      body,
      callbackBuilder,
      resultValue,
      debug,
      debugText,
    } = this.options;
    const { successAction, failedAction } = this;
    const urlOption = this.options.url;

    // Build basic request options
    const reqParams = {};
    if (method) {
      reqParams.method = method;
    }
    if (body && typeof body !== "function") {
      reqParams.body = body;
    }

    // Build the saga function
    return function* generatedSaga(action) {
      if (debug) {
        // eslint-disable-next-line
        console.log(`running saga: ${debugText}`);
      }

      // Build url and body based on callback function.
      if (typeof body === "function") {
        reqParams.body = body(action);
      }

      // let urlString
      let url = "";
      if (typeof urlOption === "function") {
        url = urlOption(action);
      } else {
        url = urlOption;
      }

      try {
        // Make the request
        const result = yield call(authorizedRequest, url, reqParams);

        // Build the result object passed with the success action.
        const resultObj = {};

        if (typeof resultValue === "function") {
          resultObj[resultKey] = resultValue(action, result);
        } else if (resultValue === null || resultValue === undefined) {
          resultObj[resultKey] = result;
        } else if (typeof resultValue === "object") {
          resultObj[resultKey] = resultValue;
        }

        // Put the success action
        yield put(generic(successAction, resultObj));

        // Build and call a callback if it exists
        if (callbackBuilder) {
          const callback = callbackBuilder(action, result);
          yield call(callback);
        }
      } catch (e) {
        if (debug) {
          // eslint-disable-next-line no-console
          console.error(`saga error: ${debugText}`, e);
        }
        // Put the failed action.
        yield put(generic(failedAction, { reason: e.response.data }));
      }
    };
  }
}
